import { useState } from "react";
import { Link } from "react-router-dom";
import "./Footer.css";

const legalDocs = {
  terms: {
    title: "Terms of Use",
    content: `Effective Date: 31.03.2026 | Last Updated: 24.03.2026

1. ABOUT US

This website, bookingalb.com, is operated by Book Albania.

Business Name: Book Albania
Website: bookingalb.com
Email: info@bookingalb.com
Phone: +355 69 434 3590
Address: Ali Demi 144, 1, 2, Tirana, Albania

Book Albania operates this website as a platform for selected accommodation listings and, where available, related travel and vehicle inquiry services.

2. ACCEPTANCE OF THESE TERMS

By accessing or using this website, you agree to these Terms of Use. If you do not agree, please do not use the website.

3. USE OF THE WEBSITE

You agree to use the website only for lawful purposes and in a manner that does not infringe the rights of others, interfere with the website, or damage its proper operation.

You must not:
• Use false, misleading, or incomplete information
• Use the website for unlawful, abusive, fraudulent, or harmful purposes
• Attempt unauthorized access to the website, servers, or connected systems
• Copy, scrape, reproduce, or commercially exploit website content without prior written permission
• Interfere with the website's performance, security, or availability

4. WEBSITE CONTENT AND ACCURACY

We aim to present accurate and up-to-date information. However, property details, availability, pricing, features, and other service information may change and may depend partly on information supplied by independent property owners, operators, or other partners.

Book Albania does not guarantee that all content will always be free from minor errors, omissions, or temporary inaccuracies.

5. THIRD-PARTY SYSTEMS AND PROVIDERS

Certain website functions may rely on third-party technology or service providers, including booking systems, payment providers, hosting services, communication tools, or other integrations.

6. INTELLECTUAL PROPERTY

Unless otherwise stated, all original content on this website is owned by or used under license by Book Albania. You may not reproduce, distribute, modify, publish, or commercially exploit such content without prior written consent.

7. BOOKING AND SERVICE-SPECIFIC TERMS

Separate terms may apply to accommodation bookings, vehicle inquiries, reservation coordination, payments, privacy, and cookies.

8. SUSPENSION, RESTRICTION, OR REFUSAL OF ACCESS

Book Albania may suspend, restrict, or refuse access to the website where reasonably necessary for security, maintenance, suspected fraud, abuse, legal compliance, or operational reasons.

9. DISCLAIMER OF WARRANTIES

The website is provided on an "as available" and "as is" basis. We do not guarantee that the website will always be uninterrupted, error-free, or free of technical defects.

10. LIMITATION OF LIABILITY

To the maximum extent permitted by law, Book Albania shall not be liable for indirect, incidental, special, consequential, or purely economic losses arising from use of or inability to use the website.

11. GOVERNING LAW

These Terms of Use are governed by the laws of the Republic of Albania.

12. DISPUTE RESOLUTION

If any dispute arises, the parties shall first seek to resolve it amicably. If no amicable solution is reached, the dispute shall be submitted to the competent courts of Tirana, Albania.

13. CHANGES TO THESE TERMS

Book Albania may update these Terms of Use from time to time. The updated version will be published on this website with a revised effective date.`
  },
  booking: {
    title: "Accommodation Booking Terms",
    content: `Effective Date: 31.03.2026 | Last Updated: 24.03.2026

1. SCOPE

These Accommodation Booking Terms apply to accommodation reservations made through bookingalb.com.

2. ROLE OF BOOK ALBANIA

Book Albania acts as the website operator, booking intermediary, and reservation facilitator. The accommodation service itself is provided by the relevant property owner or operator.

3. PROPERTY LISTINGS

Property descriptions, amenities, photos, availability, and other listing information are presented to the best of our knowledge and based in part on information provided by property owners or operators.

4. BOOKING PROCESS

A reservation is considered confirmed when the booking flow has been successfully completed and the reservation is recorded as accepted in the applicable system.

5. PRICING

The total price displayed before booking confirmation is the applicable reservation price, unless there has been an obvious pricing or technical error.

6. PAYMENT

Payment is processed through POK, using a booking flow supported by Beds24. Payment is due and may be charged 5 days before check-in.

7. CANCELLATION POLICY

Default cancellation policy:
• Free cancellation up to 5 days before check-in
• Cancellations made less than 5 days before check-in are non-refundable
• No-show reservations are non-refundable

8. REFUNDS

Where a refund is due, it will be processed to the original payment method where reasonably possible. Refund processing times may vary depending on the payment provider.

9. NO-SHOWS AND EARLY DEPARTURES

No-show reservations are non-refundable. Unused nights from early departures are not automatically refundable unless expressly agreed otherwise.

10. GUEST RESPONSIBILITIES

Guests must ensure all information is accurate, comply with these Terms and property rules, and are responsible for the conduct of all persons included in the booking.

11. PROPERTY RULES

Each property may have its own rules regarding check-in/check-out, occupancy, pets, smoking, parties, quiet hours, and other conditions. Guests are responsible for reviewing and complying with applicable property rules.

12. DAMAGE, MISUSE, AND RULE VIOLATIONS

Guests may be held financially responsible for damage to the property, excessive dirtiness, or serious breaches of house rules.

13. GOVERNING LAW AND DISPUTES

These Terms are governed by the laws of the Republic of Albania. Disputes shall be submitted to the competent courts of Tirana, Albania.`
  },
  autos: {
    title: "Car Inquiry & Reservation Terms",
    content: `Effective Date: 31.03.2026 | Last Updated: 24.03.2026

1. CURRENT SCOPE OF VEHICLE SERVICES

At this stage, vehicle-related services on bookingalb.com are offered as inquiry-based and coordination-based services only. Submitting a request does not create an instant confirmed vehicle rental booking.

2. ROLE OF BOOK ALBANIA

Book Albania acts as a facilitator of vehicle inquiries and reservation coordination. The actual vehicle rental service is provided by the relevant independent rental partner.

3. INQUIRY SUBMISSION

Following submission, Book Albania may contact the user to confirm availability, clarify dates and preferences, and coordinate the request with the relevant rental partner.

4. NO AUTOMATIC BOOKING

A vehicle inquiry does not become a confirmed reservation unless and until express confirmation is given through the applicable communication process.

5. PAYMENTS

At the current stage, the website does not provide a fully automated vehicle rental checkout flow. Any prepayment may be arranged separately. Final pricing and payment conditions will be communicated during the coordination process.

6. RENTAL PARTNER RESPONSIBILITY

The relevant vehicle rental partner is responsible for the vehicle rental itself, including vehicle availability, condition, insurance terms, deposit requirements, driver eligibility, and documentation requirements.

7. USER RESPONSIBILITIES

By submitting a vehicle inquiry, the user confirms that the information submitted is accurate, they are legally able to enter into a rental arrangement if confirmed, and they understand that an inquiry is not the same as a final booking.

8. RIGHT TO DECLINE OR DISCONTINUE

Book Albania may decline or discontinue a vehicle inquiry where reasonably necessary, including in cases of incomplete information, suspected fraud, or partner unavailability.

9. GOVERNING LAW AND DISPUTES

These Terms are governed by the laws of the Republic of Albania. Disputes shall be submitted to the competent courts of Tirana, Albania.`
  },
  privacy: {
    title: "Privacy Policy",
    content: `Effective Date: 31.03.2026 | Last Updated: 24.03.2026

1. WHO WE ARE

Book Albania operates bookingalb.com.

Email: info@bookingalb.com
Phone: +355 69 434 3590
Address: Ali Demi 144, 1, 2, Tirana, Albania

2. PERSONAL DATA WE MAY COLLECT

Depending on your interaction, we may collect:
• Full name, email address, phone number
• Booking details, travel dates, guest counts
• Inquiry details and communication content
• Payment-related transaction information
• Technical data necessary for website functioning

3. HOW WE USE PERSONAL DATA

We use personal data to:
• Respond to inquiries and manage bookings
• Coordinate vehicle inquiries
• Process or support payments
• Prevent fraud or misuse
• Comply with legal obligations

4. SHARING OF PERSONAL DATA

We may share personal data with property owners, vehicle rental partners, POK (payment provider), Beds24 (booking system), and legal/compliance advisors where necessary. We do not sell personal data.

5. DATA RETENTION

We retain personal data only for as long as reasonably necessary for handling reservations, payment records, legal compliance, and legitimate business record-keeping.

6. DATA SECURITY

We take reasonable technical and organizational measures to protect personal data against unauthorized access, misuse, or unlawful disclosure.

7. YOUR RIGHTS

Subject to applicable law, you may have the right to:
• Request access to your personal data
• Request correction of inaccurate data
• Request deletion where appropriate
• Object to certain processing
• Lodge a complaint with the competent supervisory authority

8. CHANGES TO THIS POLICY

We may update this Privacy Policy from time to time. Any updated version will be published on the website with a revised effective date.

9. CONTACT

Email: info@bookingalb.com
Phone: +355 69 434 3590`
  }
};

export default function Footer() {
  const [activeDoc, setActiveDoc] = useState(null);

  function openDoc(key) { setActiveDoc(key); document.body.style.overflow = "hidden"; }
  function closeDoc() { setActiveDoc(null); document.body.style.overflow = ""; }

  return (
    <>
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">

            {/* COL 1 — Logo + Description */}
            <div className="footer-brand">
              <Link to="/">
                <img src="/bookalblogo.png" alt="BookingAlb" className="footer-logo-img" />
              </Link>
              <p>Discover Albania. Handpicked villas, boutique retreats and authentic experiences across the Albanian Riviera and beyond.</p>
            </div>

            {/* COL 2 — Legal */}
            <div className="footer-col">
              <h4>Legal</h4>
              <button onClick={() => openDoc("terms")}>Terms of Use</button>
              <button onClick={() => openDoc("booking")}>Booking Terms</button>
              <button onClick={() => openDoc("autos")}>Car Inquiry Terms</button>
              <button onClick={() => openDoc("privacy")}>Privacy Policy</button>
            </div>

            {/* COL 3 — Links */}
            <div className="footer-col">
              <h4>Explore</h4>
              <Link to="/stays">Properties</Link>
              <Link to="/services">Our Services</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/autos">Autos</Link>
              <Link to="/for-owners">For Owners</Link>
            </div>

          </div>

          {/* BOTTOM BAR */}
          <div className="footer-bottom">
            <p>© 2025 BookingAlb. All rights reserved.</p>
            <div className="footer-icons">
              {/* Instagram */}
              <a href="https://www.instagram.com/bookingalb?igsh=MTByMnU1aHEyNTAzMA%3D%3D" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              {/* Pinterest */}
              <a href="https://pin.it/6wIjol2Fn" target="_blank" rel="noopener noreferrer" aria-label="Pinterest">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
              </a>
              {/* WhatsApp */}
              <a href="https://wa.me/355694343590" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              {/* Phone */}
              <a href="tel:+355694343590" aria-label="Phone">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </a>
              {/* Google Maps */}
              <a href="https://maps.google.com/?q=Ali+Demi+144+Tirana+Albania" target="_blank" rel="noopener noreferrer" aria-label="Google Maps">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              </a>
              {/* Email */}
              <a href="mailto:info@bookingalb.com" aria-label="Email">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
              </a>
            </div>
          </div>

        </div>
      </footer>

      {/* LEGAL POPUP */}
      {activeDoc && (
        <div className="legal-backdrop" onClick={closeDoc}>
          <div className="legal-modal" onClick={e => e.stopPropagation()}>
            <div className="legal-header">
              <h2>{legalDocs[activeDoc].title}</h2>
              <button className="legal-close" onClick={closeDoc}>✕</button>
            </div>
            <div className="legal-body">
              {legalDocs[activeDoc].content.split('\n').map((line, i) => (
                <p key={i} className={line.match(/^\d+\./) ? "legal-section-title" : line.startsWith('•') ? "legal-bullet" : ""}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}